import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { getRandomId } from '../../../../common/utils/utils';
import { polygonEpsilonAtom } from '../../../labelling-v2/atoms/tool-atoms';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import useAnalyticsForSAM from '../../analytics/useSAMAnalytics';
import { getSAM3LabelAnalyticsPayload } from '../../components/toolbar/utils';
import { ANNOTATION_TYPE, SAM_ANNOTATION_ACTIONS } from '../../constants';
import type { BulkAnnotationT } from '../../types/annotations.type';
import { base64MaskToPolygonResult, DEFAULT_POLYGON_EPSILON, douglasPeucker } from '../../utils/mask-to-polygon';
import type { Sam3AutoDetectBuildPendingItemArgsT, Sam3CommittedPendingItemT } from './use-sam3-auto-detect';
import { useSam3AutoDetect } from './use-sam3-auto-detect';
import useStage from './use-stage';
type PendingPolygon = {
  points: [number, number][];
  rawContour: [number, number][];
};
export const useSam3ForAutoDetectPolygon = () => {
  const {
    bulkAddAnnotations
  } = useStage();
  const {
    currentFrameAtom
  } = useStageAtoms();
  const currentFrame = useAtomValue(currentFrameAtom);
  const polygonEpsilon = useAtomValue(polygonEpsilonAtom);
  const {
    sendEventSam3MaskToPolygon,
    sendEventSam3TightnessAdjusted
  } = useAnalyticsForSAM();
  const buildPendingItem = useCallback(async ({
    result,
    selectedAnnotationLabel,
    textPrompt
  }: Sam3AutoDetectBuildPendingItemArgsT) => {
    if (!result.mask) return undefined;
    const rawBase64Mask = result.mask.startsWith('data:') ? result.mask.slice(result.mask.indexOf(',') + 1) : result.mask;
    const {
      points,
      rawContour,
      rawPointCount,
      simplifiedPointCount,
      epsilonUsed
    } = await base64MaskToPolygonResult(rawBase64Mask);
    sendEventSam3MaskToPolygon({
      ...getSAM3LabelAnalyticsPayload(selectedAnnotationLabel),
      samTool: 'AUTO_DETECT',
      annotationType: 'POLYGON',
      success: points.length > 0,
      rawPointCount,
      simplifiedPointCount,
      epsilonUsed
    });
    if (points.length === 0) return undefined;
    return {
      pending: {
        points,
        rawContour
      },
      reviewEntry: {
        type: SAM_ANNOTATION_ACTIONS.AUTO_DETECT_POLYGON,
        annotationMetaData: [{
          annotationLabel: selectedAnnotationLabel,
          textPrompt
        }],
        annotationData: [{
          points
        }]
      }
    };
  }, [sendEventSam3MaskToPolygon]);
  const onSubmitAll = useCallback((pending: Sam3CommittedPendingItemT<PendingPolygon>[]) => {
    const [firstPending] = pending;
    if (!firstPending) return;
    const annotations: BulkAnnotationT[] = [{
      frameIndex: currentFrame,
      annotations: pending.map(({
        data,
        annotationLabel
      }) => ({
        id: getRandomId(),
        annotation: {
          annotationType: ANNOTATION_TYPE.POLYGON,
          annotationLabel,
          data: {
            points: data.points
          },
          locked: false
        }
      }))
    }];
    bulkAddAnnotations(annotations);
    if (polygonEpsilon !== DEFAULT_POLYGON_EPSILON) {
      sendEventSam3TightnessAdjusted({
        ...getSAM3LabelAnalyticsPayload(firstPending.annotationLabel),
        annotationType: 'POLYGON',
        epsilon: polygonEpsilon
      });
    }
  }, [bulkAddAnnotations, currentFrame, polygonEpsilon, sendEventSam3TightnessAdjusted]);
  const {
    updateAllPending,
    ...rest
  } = useSam3AutoDetect<PendingPolygon>({
    annotationType: 'POLYGON',
    buildPendingItem,
    onSubmitAll
  });
  const updateEpsilon = useCallback((epsilon: number) => {
    updateAllPending(data => {
      const points = data.rawContour.length > 0 ? douglasPeucker(data.rawContour, epsilon) : data.points;
      return {
        data: {
          ...data,
          points
        },
        reviewEntry: {
          annotationData: [{
            points
          }]
        }
      };
    });
  }, [updateAllPending]);
  return {
    ...rest,
    updateEpsilon
  };
};